/**
 * Normalize a CRM account subdomain (for example Kommo or SalesDrive).
 *
 * Users routinely paste the whole account URL (`https://acme.kommo.com/leads`) into a "Subdomain"
 * field instead of just `acme`. A malformed subdomain breaks provider URLs (and, for Kommo, its
 * OAuth authorize/token exchange), so we strip the protocol, any path/query/fragment, and the
 * domain suffix — keeping only the first label.
 *
 * Idempotent: `normalizeSubdomain(normalizeSubdomain(x)) === normalizeSubdomain(x)`.
 * Applied on both the client (live field correction) and the server (defense-in-depth at every
 * persist boundary), so an API client can't persist a broken subdomain either.
 *
 * Lowercased: provider account subdomains are case-insensitive. Kommo's OAuth callback also compares
 * the stored value against its `referer` hint with a case-sensitive `startsWith`, so a stored `Acme`
 * vs a returned `acme.kommo.com` would falsely trip the `subdomain_mismatch` guard.
 */
export function normalizeSubdomain(raw: string): string {
  return raw
    .trim()
    .replace(/^https?:\/\//i, '') // strip protocol
    .split(/[/?#]/)[0] // strip path / query / fragment
    .split('.')[0] // keep the first label only (drops `.kommo.com`, `.amocrm.ru`, …)
    .trim()
    .toLowerCase()
}

/**
 * Return a shallow copy of `record` with its `subdomain` field normalized, if present. Used at the
 * generic CRM create/update API boundary to defend the long-token path the same way the OAuth
 * prepare route is defended. No-op when `record` is null/undefined or carries no string subdomain.
 */
export function normalizeSubdomainInRecord<T extends Record<string, unknown> | null | undefined>(
  record: T,
): T {
  if (!record || typeof record.subdomain !== 'string') return record
  return { ...record, subdomain: normalizeSubdomain(record.subdomain) }
}
